import React, { useState } from "react"
import { useDispatch } from "react-redux"
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined"
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"
import { setToastes } from "StoreRedux/Slice/ToastSlice"
import { InfoDescuentoLink } from "utils/DescuentosEventoQuery"
import { getDatosUsuariosLocalStorag } from "utils/DatosUsuarioLocalStorag"
import { GuardarDescuentoLinkPublico, ObtenerDescuentoLinkPublico } from "utils/CarritoLocalStorang"
import { textoDescuento } from "utils/descuentoTexto"

/* "Código de descuento": el cliente escribe o pega el codigo del evento (el mismo que va dentro del link/QR de
   descuento) y, si es valido para ESTE evento, queda aplicado igual que si hubiera entrado por el link.
   Discreto a proposito (un boton de contorno, en el estilo Bootstrap de la pagina) pero reconocible: con icono,
   texto claro y area de toque comoda. El descuento puede ser un % o un monto fijo en dolares (ver descuentoTexto).
   `onCambio` se llama al aplicar o quitar (para que el resumen de precios se vuelva a calcular). */

export const limpiarCodigoEscrito = (v) => String(v ?? "").trim().toUpperCase().replace(/\s+/g, "")

const ICONO = { fontSize: "1.1em", verticalAlign: "-0.2em" }

function CodigoDescuento({ codigoEvento, onCambio }) {
  const dispatch = useDispatch()
  const [abierto, setAbierto] = useState(false)
  const [codigo, setCodigo] = useState("")
  const [cargando, setCargando] = useState(false)
  const [error, setError] = useState("")
  const [, setVersion] = useState(0)

  // Un descuento que eligio un vendedor a mano (panel interno) no se mezcla con un codigo
  let vendedorEligio = false
  try { vendedorEligio = !!JSON.parse(sessionStorage.getItem("descuentoVentaSel") || "null")?.id } catch (_e) { /* sin datos */ }
  if (vendedorEligio) return null

  const aplicado = ObtenerDescuentoLinkPublico(codigoEvento)

  const aplicar = async () => {
    const cod = limpiarCodigoEscrito(codigo)
    if (!cod) { setError("Escribe tu código de descuento"); return }
    setCargando(true)
    setError("")
    let cedula = ""
    try { cedula = getDatosUsuariosLocalStorag()?.cedula || "" } catch (_e) { /* sin datos */ }
    try {
      const resp = await InfoDescuentoLink(cod, cedula, codigoEvento)
      const info = resp?.data
      if (resp?.success && info?.valido) {
        GuardarDescuentoLinkPublico(info, cod)
        setCodigo("")
        setAbierto(false)
        setVersion((n) => n + 1)
        dispatch(setToastes({
          show: true,
          message: `Código ${cod} aplicado: ${textoDescuento(info)}. Lo verás detallado al pagar.`,
          color: "bg-success",
          estado: "Descuento aplicado",
        }))
        if (onCambio) onCambio()
      } else {
        setError(info?.motivo || "Código de descuento no válido")
      }
    } catch (e) {
      setError(e?.response?.status === 429
        ? "Demasiados intentos. Espera un minuto e inténtalo de nuevo"
        : "No se pudo validar el código. Inténtalo de nuevo")
    } finally {
      setCargando(false)
    }
  }

  const quitar = () => {
    try {
      sessionStorage.removeItem("descuentoLinkPublico")
      const sel = JSON.parse(sessionStorage.getItem("descuentoVentaSel") || "null")
      if (sel && sel.codigo) sessionStorage.removeItem("descuentoVentaSel")
    } catch (_e) { /* sin datos */ }
    setVersion((n) => n + 1)
    if (onCambio) onCambio()
  }

  const cerrar = () => { setAbierto(false); setCodigo(""); setError("") }

  // Ya aplicado: una linea con lo que se obtuvo y la forma de quitarlo
  if (aplicado) {
    return (
      <div className="mt-2 text-center">
        <div className="alert alert-success d-inline-flex flex-wrap align-items-center justify-content-center py-1 px-3 mb-0" role="status" style={{ fontSize: "0.875rem", gap: 8 }}>
          <span>
            <CheckCircleOutlineIcon style={ICONO} aria-hidden="true" />{" "}
            Código <strong>{aplicado.codigo}</strong> · {textoDescuento(aplicado)}
          </span>
          <button type="button" className="btn btn-link btn-sm p-0" style={{ textDecoration: "underline" }} onClick={quitar}>Quitar</button>
        </div>
      </div>
    )
  }

  // Cerrado: boton de contorno, discreto pero visible
  if (!abierto) {
    return (
      <div className="mt-2 text-center">
        <button type="button" className="btn btn-outline-secondary btn-sm px-3" style={{ fontSize: "0.875rem", borderRadius: 50 }} onClick={() => setAbierto(true)}>
          <LocalOfferOutlinedIcon style={ICONO} aria-hidden="true" /> Código de descuento
        </button>
      </div>
    )
  }

  // Abierto: etiqueta, campo y boton juntos; el error va pegado al campo
  return (
    <div className="mt-2 text-center">
      <div className="d-inline-block text-start" style={{ maxWidth: 320, width: "100%" }}>
        <label htmlFor="codigoDescuentoInput" className="mb-1 text-muted d-block" style={{ fontSize: "0.8rem" }}>¿Tienes un código de descuento?</label>
        <div className="input-group input-group-sm">
          <input
            id="codigoDescuentoInput"
            type="text"
            className={`form-control${error ? " is-invalid" : ""}`}
            style={{ textTransform: "uppercase" }}
            placeholder="Escribe o pega tu código"
            maxLength={24}
            autoFocus
            autoComplete="off"
            aria-describedby={error ? "codigoDescuentoError" : undefined}
            value={codigo}
            disabled={cargando}
            onChange={(e) => { setCodigo(e.target.value); setError("") }}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); aplicar() }
              if (e.key === "Escape") cerrar()
            }}
          />
          <button type="button" className="btn btn-success" disabled={cargando} onClick={aplicar}>
            {cargando ? "..." : "Aplicar"}
          </button>
        </div>
        {error && <div id="codigoDescuentoError" role="alert" className="text-danger mt-1" style={{ fontSize: "0.8rem" }}>{error}</div>}
        <div className="text-end mt-1">
          <button type="button" className="btn btn-link btn-sm p-0 text-muted" disabled={cargando} onClick={cerrar}>Cancelar</button>
        </div>
      </div>
    </div>
  )
}

export default CodigoDescuento
